import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  linkedSignal,
  NgZone,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { L10N_LOCALE, L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { firstValueFrom } from 'rxjs';
import { DashboardContext } from '../state/dashboard-context';
import { DashboardWidgetRegistry } from '../widget/dashboard-widget-registry';
import { DEFAULT_REFRESH_INTERVAL_SECONDS } from '../layout/dashboard-layout';
import { filterDefaultOf } from '../state/dashboard-state';
import { MAX_SAVED_VIEWS } from '../views/dashboard-view';
import { LayoutService } from '../../layout/layout.service';
import { DashboardViews } from '../views/dashboard-views';
import { describeInterval } from '../host/describe-interval';
import { DashboardCustomizeSession } from './dashboard-customize-session';
import { scrollContainerOf } from './scroll-container';
import { InputPromptComponent, InputPromptData } from '../../modals/input-prompt/input-prompt.component';

const PANEL_GAP_PX = 16;

/** The Customize side sheet. It lasts as long as Customize mode and closing it cancels. */
@Component({
  selector: 'app-dashboard-customize-panel',
  templateUrl: './dashboard-customize-panel.component.html',
  styleUrls: ['./dashboard-customize-panel.component.scss'],
  imports: [
    L10nTranslatePipe,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatSelectModule,
    MatSlideToggleModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'complementary',
    'aria-labelledby': 'dashboard-customize-panel-title',
    '[style.--dashboard-panel-top.px]': 'stickyTop()',
    '[style.min-height]': 'minHeight()',
    '(keydown.escape)': 'onEscape($event)',
    '(window:resize)': 'measure()',
  },
})
export class DashboardCustomizePanelComponent {
  protected readonly session = inject(DashboardCustomizeSession);
  protected readonly views = inject(DashboardViews);
  private readonly registry = inject(DashboardWidgetRegistry);
  private readonly context = inject(DashboardContext);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly translation = inject(L10nTranslationService);
  private readonly dialog = inject(MatDialog);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly layoutService = inject(LayoutService, { optional: true });
  private readonly injector = inject(Injector);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly locale = inject(L10N_LOCALE);

  private readonly editedView = this.session.editedView;
  protected readonly editingSavedView = computed(() => !!this.editedView());
  protected readonly cannotSaveNew = computed(() => this.views.busy() || this.views.atLimit() || this.views.readOnly());
  /** mat-select treats a null option as "no selection", so the Default view gets a value no saved view can have */
  protected readonly defaultViewOption = '';

  private readonly layout = this.session.layout;
  protected readonly catalog = computed(() => this.registry.catalog(this.layout()?.widgets ?? []));
  protected readonly filters = computed(() =>
    this.context
      .filterFields(this.layout()?.filters ?? [])
      .map((field) => ({ field, value: filterDefaultOf(field, this.layout()?.filterDefaults) })),
  );
  protected readonly autoRefresh = computed(() => !!this.layout()?.refresh?.enabled);
  protected readonly refreshLabel = computed(() =>
    describeInterval(this.layout()?.refresh?.intervalSeconds ?? DEFAULT_REFRESH_INTERVAL_SECONDS, this.translation),
  );

  private readonly toolbarBottom = this.layoutService?.layoutHeightOffsetTop ?? 0;
  private scroller = document.documentElement;
  /** Relative to the scroll container, which may already start below the toolbar */
  protected readonly stickyTop = signal(PANEL_GAP_PX);
  /** In the scroll container's content, measured when the panel opens */
  private readonly pageTop = signal(0);
  private readonly pageBelow = signal(0);
  private readonly scrollportHeight = signal(0);
  protected readonly minHeight = computed(() =>
    this.scrollportHeight() ? `${this.scrollportHeight() - this.pageTop() - this.pageBelow()}px` : null,
  );

  protected readonly maxViews = MAX_SAVED_VIEWS;
  /** Rename changes it; Save saves it with the draft */
  protected readonly draftName = linkedSignal(() => this.editedView()?.name ?? '');
  protected readonly confirmingDelete = linkedSignal({ source: this.editedView, computation: () => false });
  protected readonly confirmingReset = signal(false);

  private readonly sheet = viewChild.required<string, ElementRef<HTMLElement>>('sheet', { read: ElementRef });
  private readonly closeButton = viewChild.required<string, ElementRef<HTMLButtonElement>>('closeButton', {
    read: ElementRef,
  });
  private readonly menuTrigger = viewChild.required<string, ElementRef<HTMLButtonElement>>('menuTrigger', {
    read: ElementRef,
  });
  private readonly keepButton = viewChild<string, ElementRef<HTMLButtonElement>>('keepButton', { read: ElementRef });
  private readonly resetButton = viewChild<string, ElementRef<HTMLButtonElement>>('resetButton', { read: ElementRef });
  private readonly keepViewsButton = viewChild<string, ElementRef<HTMLButtonElement>>('keepViewsButton', {
    read: ElementRef,
  });

  constructor() {
    afterNextRender(() => {
      this.closeButton().nativeElement.focus({ preventScroll: true });
      this.scroller = scrollContainerOf(this.host.nativeElement);
      this.pageTop.set(this.contentTopOf(this.host.nativeElement.getBoundingClientRect().top));
      this.measure();
      this.fitSheetOnScroll();
      // Measure once the min-height from pageTop applies; before that the page may be exactly the window's height
      afterNextRender(() => this.measurePageBelow(), { injector: this.injector });
    });
  }

  focusClose(): void {
    this.focusLater(() => this.closeButton().nativeElement);
  }

  protected saveOver(): void {
    void this.session.save(this.draftName());
  }

  protected async saveAsNew(): Promise<void> {
    const label = this.editingSavedView() ? 'Dashboard.Panel.SaveAsNew' : 'Dashboard.Panel.SaveView';
    const name = await this.askForName(label, label, this.views.suggestName(), null);
    if (name !== null) {
      await this.session.saveAsNew(name);
    }
  }

  protected async rename(): Promise<void> {
    const name = await this.askForName(
      'Dashboard.Panel.RenameView',
      'Dashboard.Panel.Rename',
      this.draftName(),
      this.editedView()?.id ?? null,
    );
    if (name !== null) {
      this.draftName.set(name);
    }
  }

  protected selectView(option: string): void {
    void this.session.switchView(option === this.defaultViewOption ? null : option);
  }

  protected askToDelete(): void {
    this.confirmingDelete.set(true);
    this.focusLater(() => this.keepButton()?.nativeElement);
  }

  protected keepView(): void {
    this.confirmingDelete.set(false);
    this.focusLater(() => this.menuTrigger().nativeElement);
  }

  protected askToReset(): void {
    this.confirmingReset.set(true);
    this.focusLater(() => this.keepViewsButton()?.nativeElement);
  }

  protected keepSavedViews(): void {
    this.confirmingReset.set(false);
    this.focusLater(() => this.resetButton()?.nativeElement);
  }

  protected async resetSavedViews(): Promise<void> {
    await this.session.resetSavedViews();
    this.confirmingReset.set(false);
    this.focusLater(() => this.closeButton().nativeElement);
  }

  protected onEscape(event: Event): void {
    // An open mat-select keeps focus on its trigger; let it close itself first
    if ((event.target as HTMLElement | null)?.closest('[aria-expanded="true"]')) {
      return;
    }

    this.session.cancel();
  }

  /** `replacing` is the view that may keep its own name */
  private async askForName(title: string, ok: string, value: string, replacing: string | null): Promise<string | null> {
    const dialog = this.dialog.open<InputPromptComponent, InputPromptData, string | false>(InputPromptComponent, {
      width: '400px',
      viewContainerRef: this.viewContainerRef,
      data: {
        title,
        ok,
        cancel: 'Dashboard.Panel.Cancel',
        okColor: 'primary',
        requireInput: true,
        inputPlaceholder: 'Dashboard.Panel.ViewName',
        value,
        validate: (name) => this.views.nameError(name, replacing),
      },
    });
    const name = await firstValueFrom(dialog.afterClosed());
    return typeof name === 'string' ? name : null;
  }

  protected measure(): void {
    const scrollport = this.scrollport();
    this.scrollportHeight.set(scrollport.bottom - scrollport.top);
    this.stickyTop.set(PANEL_GAP_PX + Math.max(0, this.toolbarBottom - scrollport.top));
    afterNextRender(() => this.fitSheet(), { injector: this.injector });
  }

  private measurePageBelow(): void {
    const bottom = this.contentTopOf(this.host.nativeElement.getBoundingClientRect().bottom);
    this.pageBelow.set(Math.max(0, this.scroller.scrollHeight - bottom));
  }

  /** Scroll events don't bubble, so listen while capturing to hear the scroll container too; the host resizes when
   * widgets change or the nav drawer toggles */
  private fitSheetOnScroll(): void {
    const fit = (): void => this.fitSheet();
    const options = { capture: true, passive: true };
    const resizes = new ResizeObserver(fit);
    this.zone.runOutsideAngular(() => {
      document.addEventListener('scroll', fit, options);
      resizes.observe(this.host.nativeElement);
    });
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('scroll', fit, options);
      resizes.disconnect();
    });
  }

  /**
   * Pins the sheet below the toolbar while the dashboard scrolls past, since `position: sticky` stops at the first
   * ancestor that clips overflow (Balsa's mat-sidenav-content and -container). Ends it a gap above the visible
   * bottom wherever it sits, so Save never needs a scroll.
   */
  private fitSheet(): void {
    const sheet = this.sheet().nativeElement.style;
    const host = this.host.nativeElement.getBoundingClientRect();
    const scrollport = this.scrollport();
    const pinTop = scrollport.top + this.stickyTop();
    const scrolledPast = host.top < pinTop;
    const height = Math.min(globalThis.innerHeight, scrollport.bottom) - PANEL_GAP_PX - Math.max(host.top, pinTop);
    const pinned = scrolledPast && host.bottom - height >= pinTop;

    sheet.height = `${height}px`;
    sheet.position = pinned ? 'fixed' : '';
    sheet.top = pinned ? `${pinTop}px` : '';
    sheet.left = pinned ? `${host.left}px` : '';
    sheet.width = pinned ? `${host.width}px` : '';
    // Past the pinned stretch it rides the bottom of the host, as a sticky element would
    sheet.transform = scrolledPast && !pinned ? `translateY(${host.height - Math.min(height, host.height)}px)` : '';
  }

  /** The scroll container's visible area, in window coordinates */
  private scrollport(): { top: number; bottom: number } {
    if (this.scroller === document.documentElement) {
      return { top: 0, bottom: globalThis.innerHeight };
    }

    const top = this.scroller.getBoundingClientRect().top + this.scroller.clientTop;
    return { top, bottom: top + this.scroller.clientHeight };
  }

  private contentTopOf(windowTop: number): number {
    return windowTop - this.scrollport().top + this.scroller.scrollTop;
  }

  private focusLater(target: () => { focus(): void } | undefined): void {
    afterNextRender(() => target()?.focus(), { injector: this.injector });
  }
}
